import { useRefinementList } from "react-instantsearch-hooks-web";
import styles from "./RefinementList.module.scss";
import { useEffect, useState } from "react";
import { useSpecials } from "../../context/specials";
import ChevronDown from "/packages/icons/chevron-down.svg";
import ChevronUp from "/packages/icons/chevron-up.svg";

export default function CustomRefinementList(props) {
  const { specials } = useSpecials();
  const { title, attribute, search } = props;
  const { items, refine, searchForItems, isFromSearch } = useRefinementList(props);

  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Remove unwanted items
  const toBeRemoved = items
    .map((item) => item.label)
    .filter((label) => /-\d+$/.test(label) || /^(sale|best_selling)$/i.test(label));

  function onChangeValue(e) {
    refine(e.target.value);
  }

  function onSearch(e) {
    searchForItems(e);
  }

  const weightOrder = [
    "half gram",
    "gram",
    "two gram",
    "eighth ounce",
    "quarter ounce",
    "half ounce",
    "ounce",
  ];

  function getWeightOrderIndex(label) {
    return weightOrder.indexOf(label.toLowerCase());
  }

  // Filter and sort weights
  const [sortedWeights, setSortedWeights] = useState([]);
  useEffect(() => {
    if (attribute === "available_weights") {
      const orderedItems = items
        .filter((item) => weightOrder.includes(item.label.toLowerCase()))
        .sort((a, b) => getWeightOrderIndex(a.label) - getWeightOrderIndex(b.label));
      setSortedWeights(orderedItems);
    }
  }, [attribute, items]);

  // Process specials from Algolia
  const getAlgoliaSpecials = (attribute, items, specials) => 
    attribute === "applicable_special_ids"
      ? items
          .filter((item) => specials?.some((special) => special.id.toString() === item.label))
          .map((sharedItem) => {
            const special = specials.find((special) => special.id.toString() === sharedItem.label);
            return {
              ...sharedItem,
              label: special?.title,
              highlighted: special?.title,
            };
          })
      : [];
  
  const algoliaSpecials = getAlgoliaSpecials(attribute, items, specials);

  // Function to group categories & subcategories
  const groupedCategories = items.reduce((acc, item) => {
    const [category, subcategory] = item.label.split(":");
    if (!subcategory) {
      acc[category] = acc[category] || { category, subcategories: [] };
    } else {
      if (!acc[category]) {
        acc[category] = { category, subcategories: [] };
      }
      acc[category].subcategories.push({ ...item, label: subcategory });
    }
    return acc;
  }, {});

  // Convert grouped object into an array
  const categoryList = Object.values(groupedCategories);
  

  // Decide what data to use based on the attribute
  let data;
  switch (attribute) {
    case "applicable_special_ids":
      data = algoliaSpecials;
      break;
    case "available_weights":
      data = sortedWeights;
      break;
    default:
      switch (title) {
        case "subcategory":
          data = items.filter((item) => item.label.includes(":")); // Only subcategories
          break;
        default:
          data = items.filter(
            (item) => !toBeRemoved.some((label) => item.label.includes(label)) && !item.label.includes(":")
          ); // Only categories in main list
          break;
      }
      break;
  }

  if (data.length === 0 && !isFromSearch) {
    return null;
  } else {
    return (
      <>
        {search && (
          <div className={styles.searchBoxContainer}>
            <div className="refinementSearchBox">
              <input placeholder="Search" onChange={(e) => onSearch(e.target.value)} />
            </div>
          </div>
        )}

        <ul className={styles.refinement_list}>
          {data.map((item) => {
            if (attribute === "applicable_special_ids") {
              // Render Special Deals
              return (
                <li key={item.value}>
                  <input
                    id={item.value}
                    type="checkbox"
                    className="checkmark"
                    value={item.value}
                    checked={item.isRefined}
                    onChange={onChangeValue}
                  />
                  <label htmlFor={item.value}>{item.label}</label>
                </li>
              );
            } else if (title === "subcategory") {
              // Render standalone Subcategories in Subcategory list
              return (
                <li key={item.value}>
                  <input
                    id={item.value}
                    type="checkbox"
                    className="checkmark"
                    value={item.value}
                    checked={item.isRefined}
                    onChange={onChangeValue}
                  />
                  <label htmlFor={item.value}>{item.label.split(":")[1]}</label>
                </li>
              );
            } else if (title === "category") {
              // Render Categories & Nested Subcategories
              const categoryData = categoryList.find((c) => c.category === item.label);
              return (
                <li key={item.value} className={styles.category_item}>
                  <div className={styles.category_toggle}>
                    <input 
                      id={item.value}
                      type="checkbox"
                      className="checkmark"
                      value={item.value}
                      checked={item.isRefined}
                      onChange={onChangeValue}
                    />
                    <label htmlFor={item.value}>{item.label}</label>
                    {categoryData?.subcategories.length > 0 && (
                      <button
                        type="button"
                        className={styles.toggle_button}
                        onClick={() => toggleCategory(item.label)}
                      >
                        {expandedCategories[item.label] ? <ChevronUp /> : <ChevronDown />}
                      </button>
                    )}
                  </div>

                  {expandedCategories[item.label] && categoryData?.subcategories.length > 0 && (
                    <ul className={styles.subcategory_list}>
                      {categoryData.subcategories.map((sub) => (
                        <li key={sub.value} className={styles.subcategory_item}>
                          <input
                            id={sub.value}
                            type="checkbox"
                            className="checkmark"
                            value={sub.value}
                            checked={sub.isRefined}
                            onChange={onChangeValue}
                          />
                          <label htmlFor={sub.value}>{sub.label}</label>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            } else {
              // Render Normal List Items
              return (
                <li key={item.value}>
                  <input
                    id={item.value}
                    type="checkbox"
                    className="checkmark"
                    value={item.value}
                    checked={item.isRefined}
                    onChange={onChangeValue}
                  />
                  <label style={item.label?.toLocaleLowerCase() === 'cbd' ? { textTransform: 'uppercase' } : null} htmlFor={item.value}>{item.label}</label>
                </li>
              );
            }
          })}
        </ul>
      </>
    );
  }
}
